import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { SharedPetitionAnglesByTokenQuery, SharedPetitionAnglesByTokenQueryVariables } from './api.graphql';
import { SharedPetitionAnglesByToken } from './api.graphql';
import type { SharedPetitionAnglesBySlugQuery, SharedPetitionAnglesBySlugQueryVariables } from './apiBySlug.graphql';
import { SharedPetitionAnglesBySlug } from './apiBySlug.graphql';
import type {
	SharedPetitionAnglesBySlugWithDecisionMakerQuery,
	SharedPetitionAnglesBySlugWithDecisionMakerQueryVariables,
} from './apiBySlugWithDecisionMaker.graphql';
import { SharedPetitionAnglesBySlugWithDecisionMaker } from './apiBySlugWithDecisionMaker.graphql';
import type {
	SharedPetitionAnglesByTokenWithDecisionMakerQuery,
	SharedPetitionAnglesByTokenWithDecisionMakerQueryVariables,
} from './apiWithDecisionMaker.graphql';
import { SharedPetitionAnglesByTokenWithDecisionMaker } from './apiWithDecisionMaker.graphql';
import type { PetitionAnglesPageData, PetitionAnglesPageDataWithDecisionMaker } from './types';
import { PetitionAnglesPageDataError } from './types';

type QueryResponse =
	| SharedPetitionAnglesByTokenQuery['socialStarterPetitionAnglesByToken']
	| SharedPetitionAnglesBySlugQuery['socialStarterPetitionAnglesBySlug']
	| SharedPetitionAnglesByTokenWithDecisionMakerQuery['socialStarterPetitionAnglesByToken']
	| SharedPetitionAnglesBySlugWithDecisionMakerQuery['socialStarterPetitionAnglesBySlug'];
type QueryResponseWithDecisionMaker =
	| SharedPetitionAnglesByTokenWithDecisionMakerQuery['socialStarterPetitionAnglesByToken']
	| SharedPetitionAnglesBySlugWithDecisionMakerQuery['socialStarterPetitionAnglesBySlug'];
type QueryFeatureConfigs = SharedPetitionAnglesByTokenQuery['featureConfigs'];

const TOKEN_RE = /^[0-9a-f]{64}$/;

export type GetPetitionAnglesPageDataOptions = {
	/**
	 * When `true`, throws `social_starter_not_found` if the record has no angles (today's
	 * social-starter-draft behavior). When `false`, a record with zero angles is returned
	 * successfully — callers that only need the starter's `username`/`sourceLocation`/`token`
	 * (e.g. the ambassador /start page) don't require any angles to exist.
	 */
	readonly requireAngles: boolean;
	readonly includeDecisionMaker?: boolean;
};

function isPetitionAnglesToken(identifier: string): boolean {
	return TOKEN_RE.test(identifier);
}

export function getPetitionAnglesPageData(
	utilityContext: UtilityContext,
	identifier: string,
	options: GetPetitionAnglesPageDataOptions & { readonly includeDecisionMaker: true },
): Promise<PetitionAnglesPageDataWithDecisionMaker>;
export function getPetitionAnglesPageData(
	utilityContext: UtilityContext,
	identifier: string,
	options: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData>;
export async function getPetitionAnglesPageData(
	utilityContext: UtilityContext,
	identifier: string,
	options: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData | PetitionAnglesPageDataWithDecisionMaker> {
	if (isPetitionAnglesToken(identifier)) {
		return getPetitionAnglesPageDataByToken(utilityContext, identifier, options);
	}
	return getPetitionAnglesPageDataBySlug(utilityContext, identifier, options);
}

async function getPetitionAnglesPageDataByToken(
	utilityContext: UtilityContext,
	token: string,
	options: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData | PetitionAnglesPageDataWithDecisionMaker> {
	if (options.includeDecisionMaker) {
		const { data } = await utilityContext.gql.fetch<
			SharedPetitionAnglesByTokenWithDecisionMakerQuery,
			SharedPetitionAnglesByTokenWithDecisionMakerQueryVariables
		>({
			query: SharedPetitionAnglesByTokenWithDecisionMaker,
			variables: { token },
			rejectOnError: true,
		});
		return mapPageDataWithDecisionMaker(
			data.socialStarterPetitionAnglesByToken,
			token,
			data.featureConfigs,
			options.requireAngles,
		);
	}

	const { data } = await utilityContext.gql.fetch<
		SharedPetitionAnglesByTokenQuery,
		SharedPetitionAnglesByTokenQueryVariables
	>({
		query: SharedPetitionAnglesByToken,
		variables: { token },
		rejectOnError: true,
	});
	const response = data.socialStarterPetitionAnglesByToken;
	return mapPageData(response, token, data.featureConfigs, options.requireAngles);
}

async function getPetitionAnglesPageDataBySlug(
	utilityContext: UtilityContext,
	slug: string,
	options: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData | PetitionAnglesPageDataWithDecisionMaker> {
	if (options.includeDecisionMaker) {
		const { data } = await utilityContext.gql.fetch<
			SharedPetitionAnglesBySlugWithDecisionMakerQuery,
			SharedPetitionAnglesBySlugWithDecisionMakerQueryVariables
		>({
			query: SharedPetitionAnglesBySlugWithDecisionMaker,
			variables: { slug },
			rejectOnError: true,
		});
		return mapPageDataWithDecisionMaker(
			data.socialStarterPetitionAnglesBySlug,
			slug,
			data.featureConfigs,
			options.requireAngles,
		);
	}

	const { data } = await utilityContext.gql.fetch<
		SharedPetitionAnglesBySlugQuery,
		SharedPetitionAnglesBySlugQueryVariables
	>({
		query: SharedPetitionAnglesBySlug,
		variables: { slug },
		rejectOnError: true,
	});
	const response = data.socialStarterPetitionAnglesBySlug;
	return mapPageData(response, slug, data.featureConfigs, options.requireAngles);
}

function mapPageData(
	response: QueryResponse,
	fallbackToken: string,
	featureConfigs: QueryFeatureConfigs,
	requireAngles: boolean,
): PetitionAnglesPageData {
	assertResponseUsable(response, requireAngles);
	return {
		token: response.token ?? fallbackToken,
		...mapMetadata(response.metadata),
		drafts: response.angles ?? [],
		imagePrewarmEnabled: featureConfigs.socialStarterImagePrewarmEnabled,
		videoConsentCheckboxEnabled: featureConfigs.videoFirstPetitionPage,
	};
}

function mapPageDataWithDecisionMaker(
	response: QueryResponseWithDecisionMaker,
	fallbackToken: string,
	featureConfigs: QueryFeatureConfigs,
	requireAngles: boolean,
): PetitionAnglesPageDataWithDecisionMaker {
	if (!response.decisionMaker) {
		throw new PetitionAnglesPageDataError('social_starter_not_found');
	}
	return {
		...mapPageData(response, fallbackToken, featureConfigs, requireAngles),
		decisionMaker: {
			id: response.decisionMaker.id,
			displayName: response.decisionMaker.displayName,
			title: response.decisionMaker.title,
			summary: response.decisionMaker.summary,
			photoUrl: response.decisionMaker.photo?.userMedium.url ?? null,
		},
	};
}

function assertResponseUsable(
	response: {
		readonly success: boolean;
		readonly angles?: readonly unknown[] | null;
	},
	requireAngles: boolean,
): void {
	if (!response.success) {
		throw new PetitionAnglesPageDataError('social_starter_not_found');
	}
	if (requireAngles && (!response.angles || response.angles.length === 0)) {
		throw new PetitionAnglesPageDataError('social_starter_not_found');
	}
}

function mapMetadata(
	metadata: QueryResponse['metadata'],
): Pick<
	PetitionAnglesPageData,
	| 'username'
	| 'name'
	| 'platform'
	| 'avatarUrl'
	| 'videoCoverUrl'
	| 'caption'
	| 'sourceLocation'
	| 'sourceUrl'
	| 'postStats'
> {
	if (!metadata) {
		return {
			username: null,
			name: null,
			platform: null,
			avatarUrl: null,
			videoCoverUrl: null,
			caption: null,
			sourceLocation: null,
			sourceUrl: null,
			postStats: { followerCount: null, viewCount: null, commentCount: null, postedAt: null },
		};
	}
	const {
		username,
		name,
		platform,
		avatarUrl,
		videoCoverUrl,
		caption,
		sourceLocation,
		sourceUrl,
		followerCount,
		viewCount,
		commentCount,
		postedAt,
	} = metadata;
	return {
		username,
		name,
		platform,
		avatarUrl,
		videoCoverUrl,
		caption,
		sourceLocation: sourceLocation ?? null,
		sourceUrl: sourceUrl ?? null,
		postStats: {
			followerCount: followerCount ?? null,
			viewCount: viewCount ?? null,
			commentCount: commentCount ?? null,
			postedAt: postedAt ?? null,
		},
	};
}
